var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import Base from "../../../Base.js";
const STM_PROP = Symbol("STM_PROP"), unrecordedFields = {
  // This field's value is a by product of node insertion and must not be recorded here.
  // It's the node insertion operation which is recorded by STM.
  parentIndex: 1,
  // Ordered parent index is similar to parentIndex - it is recalculated on node insertion. No need to track it
  // https://github.com/bryntum/support/issues/8257
  orderedParentIndex: 1
};
var ModelStm_default = (Target) => {
  var _a;
  return _a = class extends (Target || Base) {
    static get defaultConfig() {
      return {
        stm: null
      };
    }
    joinStore(store) {
      if (!this.stm) {
        this.stm = store.stm;
      }
    }
    unjoinStore(store, isReplacing = false) {
      var _a2;
      if (this.stm === store.stm) {
        this.stm = null;
      }
      (_a2 = super.unjoinStore) == null ? void 0 : _a2.call(this, store, isReplacing);
    }
    /**
     * Reference to STM manager, if used
     * @member {Core.data.stm.StateTrackingManager}
     * @category Misc
     */
    get stm() {
      return this[STM_PROP];
    }
    set stm(stm) {
      this[STM_PROP] = stm;
    }
    // Hook for chronograph entity field accessors, for example; task.duration = 123.
    // Triggers before setting the value.
    beforeChronoFieldSet(fieldName, value) {
      var _a2;
      const me = this;
      if (!me.inSetting && ((_a2 = me.stm) == null ? void 0 : _a2.enabled) && !unrecordedFields[fieldName] && !me.constructor.nonPersistableFields[fieldName]) {
        if (me.getFieldDefinition(fieldName)) {
          return { [fieldName]: { value, oldValue: me.graph.activeTransaction.readCurrentOrProposedOrPrevious(me.$[fieldName]) } };
        }
      }
      return null;
    }
    // Hook for chronograph entity field accessors, for example; task.duration = 123
    // Triggers after setting the value.
    afterChronoFieldSet(fieldName, value, wasSet) {
      wasSet && this.afterSet(fieldName, value, false, false, wasSet, true);
    }
    shouldRecordFieldChange(fieldName, oldValue, newValue) {
      const store = this.firstStore;
      return !(this.constructor.nonPersistableFields[fieldName] || oldValue == null && newValue == null || oldValue === newValue || oldValue instanceof Date && newValue instanceof Date && oldValue.getTime() === newValue.getTime() || fieldName === "id" || fieldName === "$PhantomId" || fieldName === "parentId" && store && store.oldIdMap[oldValue] === store.getById(newValue));
    }
    /**
     * Overridden to store initial data of the changed fields and to notify STM
     * manager about the change action if anything has been changed in result.
     *
     * The method is called from within {@link @bryntum/core-thin/lib/data/Model#function-set} method.
     *
     * @private
     */
    afterSet(field, value, silent, fromRelationUpdate, wasSet, isChronoFieldSet) {
      const { stm } = this, nonPersistableFields = this.constructor.nonPersistableFields;
      if ((stm == null ? void 0 : stm.isBase) && stm.shouldRecordAction && !unrecordedFields[field] && !nonPersistableFields[field]) {
        if (wasSet) {
          let shouldRecord;
          const [newData, oldData] = Object.keys(wasSet).reduce(
            (data, fieldName) => {
              const { value: value2, oldValue } = wasSet[fieldName];
              if (this.shouldRecordFieldChange(fieldName, oldValue, value2)) {
                shouldRecord = true;
                data[0][fieldName] = value2;
                data[1][fieldName] = oldValue;
              }
              return data;
            },
            [{}, {}]
          );
          if (shouldRecord) {
            stm.onModelUpdate(this, newData, oldData, isChronoFieldSet);
          }
        }
      }
    }
    /**
     * Called from {@link @bryntum/core-thin/lib/data/mixin/TreeNode#function-insertChild} to obtain inserted
     * records initial parents and parent index, to be able to restore the state back upon undo.
     *
     * @param {Core.data.Model[]} childRecords
     * @returns {Array} Array of results from this call and any of super calls if any.
     *               This result is consumed by {@link #function-afterInsertChild} which pops
     *               from the result array to take only results of this method call and leave
     *               results from super calls untouched.
     *
     * @private
     */
    beforeInsertChild(childRecords) {
      var _a2;
      const preResult = ((_a2 = super.beforeInsertChild) == null ? void 0 : _a2.call(this, childRecords)) || [], { stm } = this;
      if (stm == null ? void 0 : stm.enabled) {
        preResult.push(
          childRecords.reduce((result, childRecord) => {
            if (childRecord.root === this.root) {
              result.set(childRecord, {
                parent: childRecord.parent,
                index: childRecord.parent ? childRecord.parentIndex : void 0
              });
            }
            return result;
          }, /* @__PURE__ */ new Map())
        );
      }
      return preResult;
    }
    /**
     * Called from {@link @bryntum/core-thin/lib/data/mixin/TreeNode#function-insertChild} to notify {@link @bryntum/core-thin/lib/data/stm/StateTrackingManager}
     * about children insertion. Provides it with all necessary context information collected
     * in {@link #function-beforeInsertChild} required to undo/redo the action.
     *
     * @private
     */
    afterInsertChild(index, childRecords, beforeResult, inserted, orderedBeforeNode) {
      var _a2;
      const { stm } = this;
      if (stm == null ? void 0 : stm.shouldRecordAction) {
        const context = beforeResult.pop();
        if (inserted) {
          stm.onModelInsertChild(this, index, inserted, context, orderedBeforeNode);
        }
      }
      (_a2 = super.afterInsertChild) == null ? void 0 : _a2.call(this, index, childRecords, beforeResult, inserted);
    }
    /**
     * Called from {@link @bryntum/core-thin/lib/data/mixin/TreeNode#function-removeChild} to obtain removed
     * records initial parent index, to be able to restore the state back upon undo.
     *
     * @param {Core.data.Model[]} childRecords
     * @param {Boolean} isMove
     * @returns {Array} Array of results from this call and any of super calls if any.
     *               This result is consumed by {@link #function-afterRemoveChild} which pops
     *               from the result array to take only results of this method call and leave
     *               results from super calls untouched.
     *
     * @private
     */
    beforeRemoveChild(childRecords, isMove) {
      const preResult = super.beforeRemoveChild ? super.beforeRemoveChild(childRecords, isMove) : [], { stm } = this;
      if ((stm == null ? void 0 : stm.shouldRecordAction) && !isMove) {
        preResult.push(
          childRecords.reduce((result, childRecord) => {
            result.set(childRecord, { parentIndex: childRecord.parentIndex, orderedParentIndex: childRecord.orderedParentIndex });
            return result;
          }, /* @__PURE__ */ new Map())
        );
      }
      return preResult;
    }
    /**
     * Called from {@link @bryntum/core-thin/lib/data/mixin/TreeNode#function-removeChild} to notify {@link @bryntum/core-thin/lib/data/stm/StateTrackingManager}
     * about children removing. Provides it with all necessary context information collected
     * in {@link #function-beforeRemoveChild} required to undo/redo the action.
     *
     * @private
     */
    afterRemoveChild(childRecords, beforeResult, isMove) {
      var _a2;
      const { stm } = this;
      if ((stm == null ? void 0 : stm.shouldRecordAction) && !isMove) {
        const context = beforeResult.pop();
        if (childRecords && childRecords.length) {
          stm.onModelRemoveChild(this, childRecords, context);
        }
      }
      (_a2 = super.afterRemoveChild) == null ? void 0 : _a2.call(this, childRecords, beforeResult, isMove);
    }
  }, __publicField(_a, "$name", "ModelStm"), _a;
};
export {
  ModelStm_default as default
};
